import { Injectable } from '@angular/core';

import { SsoScopeService } from '../sso/sso-scope.service';
import { StorageBrowser } from '../storage.service';

import {
  ALL_DEPARTMENTS_ID,
  RolesEnum,
} from 'src/app/features/tests/features/constants';
import { DEFAULT_PAGES, SUPERVISOR_EMAILS } from 'src/app/features/user-management/services/constants';
import { ROLE_PERMISSIONS } from './ability.constants';
import { Action, Subject } from './ability.type';

@Injectable({ providedIn: 'root' })
export class AbilityService {
  public subjects = Subject;
  public actions = Action;
  private readonly key = 'user';
  private readonly abilitySet = new Set<string>();
  public role: RolesEnum;
  public userId: string;
  public email: string;
  public isLoggedIn: boolean = false;
  public isDepartmentVisible: boolean;
  public departmentId?: string;
  public isMainLab: boolean = false;

  /**
   * isSSO is sticky-derived: sessionStorage `sso_scope` (set sync at SSO
   * landing) is the primary source of truth, with `/me`'s `isSSO` as a
   * fallback. This means refresh-token rotation (which may drop the claim)
   * doesn't break the kiosk UI, and guards see the right value
   * synchronously before /me resolves.
   */
  get isSSO(): boolean {
    return this.ssoScope.isActive() || !!this.storage.get(this.key)?.isSSO;
  }

  get mainLabUrl(): string | null {
    return this.ssoScope.get()?.mainLabUrl ?? this.storage.get(this.key)?.mainLabUrl ?? null;
  }

  constructor(
    private storage: StorageBrowser,
    private ssoScope: SsoScopeService,
  ) {
    this.loadRoleFromStorage();
  }

  refresh() {
    this.loadRoleFromStorage();
  }

  private loadRoleFromStorage() {
    const user = this.storage.get(this.key);
    const role = (user?.role || RolesEnum.Viewer) as RolesEnum;
    // TODO: Implement JWT decoding and role/departmentId extraction

    // const token = this.storage.get('token'); // Get the raw JWT string
    // let role = RolesEnum.Viewer; // Default fallback
    // let departmentId = null;

    // if (token) {
    //   const decoded = this.decodeJwt(token);
    //   if (decoded) {
    //     // Adjust 'role' and 'departmentId' keys based on your actual JWT payload structure
    //     role = (decoded.role || decoded.userRole || RolesEnum.Viewer) as RolesEnum;
    //     departmentId = decoded.departmentId;
    //   }
    // }

    this.role = role;
    this.isLoggedIn = !!user;
    this.departmentId = user?.departmentId;
    this.email = user?.email;
    this.userId = user?.id;
    this.isMainLab = !!user?.isMainLab;
    // isSSO and mainLabUrl are computed via getters above (sticky from
    // sessionStorage); no field assignment needed here.
    // this.departmentId = departmentId;

    if (role == RolesEnum.OutsourceScorer) {
      this.isDepartmentVisible = false;
    } else {
      this.isDepartmentVisible = user?.departmentId == ALL_DEPARTMENTS_ID;
    }

    this.abilitySet.clear();

    const abilities = ROLE_PERMISSIONS[role] || [];
    for (const [action, subject] of abilities) {
      this.abilitySet.add(`${action}:${subject}`);
    }
  }

  can(action: Action, subject: Subject): boolean {
    return (
      this.abilitySet.has(`${action}:${subject}`) ||
      this.abilitySet.has(`${action}:${Subject.All}`) ||
      this.abilitySet.has(`${Action.All}:${subject}`) ||
      this.abilitySet.has(`${Action.All}:${Subject.All}`)
    );
  }

  cannot(action: Action, subject: Subject): boolean {
    return !this.can(action, subject);
  }

  getRole(): RolesEnum {
    return this.role;
  }

  canEdit(subject: Subject): boolean {
    return this.can(Action.Edit, subject);
  }
  canView(subject: Subject): boolean {
    return this.can(Action.View, subject);
  }

  canCreate(subject: Subject): boolean {
    return this.can(Action.Create, subject);
  }

  canDelete(subject: Subject): boolean {
    return this.can(Action.Delete, subject);
  }

  isSupervisor(): boolean {
    if (!this.isLoggedIn) return false;
    return (
      SUPERVISOR_EMAILS
        .map((email) => email.toLowerCase())
        .includes(this.email.toLowerCase()) ||
      this.role === RolesEnum.OutsourceScorer
    );
  }

  isTechAdmin(): boolean {
    return this.role === RolesEnum.TechAdmin
  }

  defaultPage() {
    return DEFAULT_PAGES[this.role] || '/dashboard';
  }

  decodeJwt(token: string): any {
    try {
      const payload = token.split('.')[1]; // Extract the payload part of the JWT
      const decodedPayload = atob(payload); // Decode the Base64-encoded payload
      return JSON.parse(decodedPayload); // Parse the JSON payload
    } catch (error) {
      console.error('Invalid JWT token:', error);
      return null;
    }
  }
}
